﻿import {
  AwakeCallback,
  BackButtonCallback,
  IdentityChangedCallback,
  IdentityChangedEvent,
  NotificationCallback,
  NotificationEvent,
  QuitCallback,
  LifecycleApi,
  PauseCallback,
  ResumeCallback,
  SleepCallback,
} from './LifecycleApi'
import { Subscription } from '../rpc'

export class MockLifecycleApi implements LifecycleApi {
  private pauseCallbacks: Set<PauseCallback> = new Set<PauseCallback>()
  private resumeCallbacks: Set<ResumeCallback> = new Set<ResumeCallback>()
  private awakeCallbacks = new Set<AwakeCallback>()
  private sleepCallbacks = new Set<SleepCallback>()
  private quitCallbacks = new Set<QuitCallback>()
  private backButtonCallbacks = new Set<BackButtonCallback>()
  private identityChangedCallbacks = new Set<IdentityChangedCallback>()
  private notificationCallbacks = new Set<NotificationCallback>()

  onSleep(callback: SleepCallback): Subscription {
    this.sleepCallbacks.add(callback)
    return {
      unsubscribe: () => {
        this.sleepCallbacks.delete(callback)
      },
    }
  }

  onAwake(callback: AwakeCallback): Subscription {
    this.awakeCallbacks.add(callback)
    return {
      unsubscribe: () => {
        this.awakeCallbacks.delete(callback)
      },
    }
  }

  onPause(callback: PauseCallback): Subscription {
    this.pauseCallbacks.add(callback)
    return {
      unsubscribe: () => {
        this.pauseCallbacks.delete(callback)
      },
    }
  }

  onResume(callback: ResumeCallback): Subscription {
    this.resumeCallbacks.add(callback)
    return {
      unsubscribe: () => {
        this.resumeCallbacks.delete(callback)
      },
    }
  }

  onQuit(callback: QuitCallback): Subscription {
    this.quitCallbacks.add(callback)
    return {
      unsubscribe: () => {
        this.quitCallbacks.delete(callback)
      },
    }
  }

  triggerPauseCallbacks() {
    for (const callback of this.pauseCallbacks) {
      callback()
    }
  }

  triggerResumeCallbacks() {
    for (const callback of this.resumeCallbacks) {
      callback()
    }
  }

  triggerAwakeCallbacks() {
    for (const callback of this.awakeCallbacks) {
      callback()
    }
  }

  triggerSleepCallbacks() {
    for (const callback of this.sleepCallbacks) {
      callback()
    }
  }

  triggerQuitCallbacks() {
    for (const callback of this.quitCallbacks) {
      callback()
    }
  }

  onBackButton(callback: BackButtonCallback): Subscription {
    this.backButtonCallbacks.add(callback)
    return {
      unsubscribe: () => {
        this.backButtonCallbacks.delete(callback)
      },
    }
  }

  triggerBackButtonCallbacks(): void {
    for (const callback of this.backButtonCallbacks) {
      callback()
    }
  }

  onIdentityChanged(callback: IdentityChangedCallback): Subscription {
    this.identityChangedCallbacks.add(callback)
    return {
      unsubscribe: () => {
        this.identityChangedCallbacks.delete(callback)
      },
    }
  }

  triggerIdentityChangedCallbacks(event: IdentityChangedEvent): void {
    for (const callback of this.identityChangedCallbacks) {
      callback(event)
    }
  }

  private pendingNotification: NotificationEvent | null = null

  onNotification(callback: NotificationCallback): Subscription {
    this.notificationCallbacks.add(callback)
    // RPC parity: a tap triggered before any subscriber replays to the first
    // one, once (see RpcLifecycleApi's boot-window buffer).
    const pending = this.pendingNotification
    if (pending) {
      this.pendingNotification = null
      queueMicrotask(() => callback(pending))
    }
    return {
      unsubscribe: () => {
        this.notificationCallbacks.delete(callback)
      },
    }
  }

  /** Playground/mock parity: simulate a warm notification tap. */
  triggerNotificationCallbacks(event: NotificationEvent): void {
    if (this.notificationCallbacks.size === 0) {
      this.pendingNotification = event
      return
    }
    for (const callback of this.notificationCallbacks) {
      callback(event)
    }
  }
}
